import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type {
  GuidedAnswer,
  GuidedBloodPressureRange,
  GuidedModeAnsweredItem,
  GuidedModeCollection,
  GuidedModeDefinition,
  GuidedModeMedia,
  GuidedModeQuestionDefinition,
  GuidedModeStep,
  GuidedValidationResult,
  GuidedModeCondition,
  GuidedModeVisibilityRule,
} from '../types/guided-mode.types'
import { DEMOGRAPHIC_CONCEPT_IDS, assessmentQuestionSteps, questionForStep } from '../utils/assessment-definition'
import { evaluateCondition, isStepAnswered, isStepVisible, stepKey } from '../utils/step-engine'
import { buildQuestionOutputAnswers, toggleMultipleOption } from '../utils/option-selection'
import {
  validateBloodPressurePart,
  validateGuidedBloodPressure,
} from '../utils/blood-pressure'
import type { BloodPressurePart } from '../utils/blood-pressure'
import { dateFromTodayOffset, formatDateAnswer } from '../utils/date'
import { calculatedDateAnswers } from '../utils/calculated-date'
import { AUTO_PROGRESS_DELAY_MS, shouldAutoProgress } from '../utils/auto-progress'

const { validateObservationValue } = require(
  '@shared/clinical-logic/helpers/observationRangeValidation.js',
)

const WARNING_MESSAGE_TOKEN = 'questions.generic.message.abnormal'
const BLOCKING_MESSAGE_TOKEN = 'questions.blood_pressure.message.error'
const calculateBMI = require('@shared/clinical-logic/convert/calculations/bmiLogic')

interface UseGuidedQuestionnaireOptions {
  answers: Ref<Record<string, GuidedAnswer>>
  autoProgress: ComputedRef<boolean>
  currentIndex: Ref<number>
  currentStepIndex: ComputedRef<number>
  definition: ComputedRef<GuidedModeDefinition | null>
  onAnswerChanged: () => boolean
  onAutoAdvance: () => Promise<void> | void
  onAnswersRemoved?: (conceptIds: Set<string>) => void
  preferredWidget: (conceptId: string) => string
  translate: (token: string, fallback: string) => string
  formatDate?: (date: Date) => string
  formatNumber?: (value: number) => string
  visibleSteps: ComputedRef<GuidedModeStep[]>
  // Recorded symptom answers that stay while their question is hidden.
  keptConceptIds?: ComputedRef<string[]>
  keptSymptomConceptIds?: ComputedRef<string[]>
  settledConceptIds?: Ref<string[]>
}

export function useGuidedQuestionnaire(options: UseGuidedQuestionnaireOptions) {
  const {
    answers, autoProgress, currentIndex, currentStepIndex, definition, onAnswerChanged,
    onAutoAdvance, preferredWidget, translate, visibleSteps, keptSymptomConceptIds, settledConceptIds,
  } = options
  const bloodPressureSystolic = ref('')
  const bloodPressureDiastolic = ref('')
  const guidedDateValue = ref('')
  const widgetOverrides = ref<Record<string, string>>({})
  const numericDraftAnswers = ref<Record<string, GuidedAnswer>>({})
  const validationResults = ref<Record<string, GuidedValidationResult>>({})
  const pendingAutoAdvanceTimer = ref<ReturnType<typeof setTimeout> | null>(null)
  let autoAdvanceInFlight = false
  // Per-conceptId drafts for specialised inputs that can appear inside a
  // sequential group (e.g. `question.blood_pressure`, `question.last_menstrual_period`).
  // The flat `bloodPressureSystolic`/`bloodPressureDiastolic`/`guidedDateValue` refs above
  // keep the standalone question path working; these records mirror the same data so a
  // blood-pressure or date question rendered inside a sequential group can hold its own
  // draft values, validation, and unknown-flag without colliding with other items on the
  // same screen.
  interface BloodPressureDraft { systolic: string; diastolic: string; unknown: boolean }
  interface DateDraft { value: string }
  const bloodPressureDrafts = ref<Record<string, BloodPressureDraft>>({})
  const dateDrafts = ref<Record<string, DateDraft>>({})
  const bloodPressureValidations = ref<Record<string, GuidedValidationResult | null>>({})
  // Track the current nested question index within a sequential (non-multiselect) group
  const currentNestedIndex = ref(0)

  const currentStep = computed(() => visibleSteps.value[currentStepIndex.value] || null)
  
  // Sequential group: a group without multiselect (UI shows each question one at a time)
  const isSequentialGroup = computed(() => {
    const group = currentStep.value?.kind === 'group' ? currentStep.value : null
    return group && !group.multiselect
  })
  
  // Get the nested steps within the current sequential group, filtered by each
  // step's `visibleWhen` rule. Sequential groups often rely on child visibility
  // (for example, adult group.cs24 contains female-only menstruating and
  // conditional amenorrhea questions that should never be shown to a male
  // patient). Rendering hidden questions would force the user to provide answers
  // for inputs that should be invisible, so we filter them out before building
  // any UI state or checking completion.
  const sequentialGroupSteps = computed(() => {
    if (!isSequentialGroup.value) return []
    return visibleGroupSteps(currentStep.value!)
  })
  
  // Current nested step within the sequential group
  const currentNestedStep = computed(() => {
    if (!isSequentialGroup.value) return null
    return sequentialGroupSteps.value[currentNestedIndex.value] || null
  })
  
  // Is this the last question in the sequential group?
  const isLastInSequentialGroup = computed(() => {
    if (!isSequentialGroup.value) return false
    return currentNestedIndex.value >= sequentialGroupSteps.value.length - 1
  })
  
  // Sequential group question items - all questions displayed on one screen
  interface SequentialGroupQuestionItem {
    conceptId: string
    prompt: string
    options: Array<{ value: string; label: string }>
    collection: GuidedModeCollection
    collectionType: string
    answer: string | number | string[] | null
    selectedValues: string[]
    multiple: boolean
    displayUnit: string
    numericStep: number
    widget: string
    inputId: string
    media: GuidedModeMedia | null
    playbackRate: number
    validationSeverity: string | null
    validationMessageToken: string
    allowUnknown: boolean
    // Blood-pressure specific state. Only populated when collectionType === 'blood_pressure'.
    bloodPressureRanges: GuidedBloodPressureRange | null
    bloodPressureSystolic: string
    bloodPressureDiastolic: string
    bloodPressureSystolicInvalid: boolean
    bloodPressureDiastolicInvalid: boolean
    bloodPressureSystolicWarning: boolean
    bloodPressureDiastolicWarning: boolean
    bloodPressureUnknown: boolean
    // Date specific state. Only populated when collectionType === 'date' or 'calculated_date'.
    dateValue: string
    dateMin: string
    dateMax: string
    dateInvalid: boolean
    dateCalculated: boolean
  }
  
  const sequentialGroupQuestionItems = computed<SequentialGroupQuestionItem[]>(() => {
    if (!isSequentialGroup.value) return []
    return sequentialGroupSteps.value.map((step, index) => {
      const question = questionForStep(definition.value, step)
      const collection = step.collection || {}
      const collectionType = collection.type || ''
      if (!question) {
        return {
          conceptId: '',
          prompt: '',
          options: [],
          collection: {} as GuidedModeCollection,
          collectionType: '',
          answer: null,
          selectedValues: [],
          multiple: false,
          displayUnit: '',
          numericStep: 1,
          widget: '',
          inputId: '',
          media: null,
          playbackRate: 1,
          validationSeverity: null,
          validationMessageToken: '',
          allowUnknown: false,
          bloodPressureRanges: null,
          bloodPressureSystolic: '',
          bloodPressureDiastolic: '',
          bloodPressureSystolicInvalid: false,
          bloodPressureDiastolicInvalid: false,
          bloodPressureSystolicWarning: false,
          bloodPressureDiastolicWarning: false,
          bloodPressureUnknown: false,
          dateValue: '',
          dateMin: '',
          dateMax: '',
          dateInvalid: false,
          dateCalculated: false,
        }
      }

      const conceptId = question.conceptId
      const concept = definition.value?.concepts?.[conceptId]
      const answer = answers.value[conceptId]
      const media = step.media || null
      const responseTokens = question.responseTokens || {}
      const values = Object.keys(responseTokens).length
        ? Object.keys(responseTokens)
        : (concept?.value?.values || []).map(item => item.value)
      const options = values.map(value => ({
        value,
        label: translate(
          responseTokens[value] || `responses.${question.id.replace('question.', '')}.${value}.title`,
          formatDisplayLabel(value),
        ),
      }))
      const selectedValues = Array.isArray(answer)
        ? answer.map(String)
        : answer === undefined ? [] : [String(answer)]
      const displayUnit = collection.displayUnit
        ? translate(`units.${collection.displayUnit}.short`, collection.displayUnit)
        : ''
      const numericStep = collection.decimalPlaces
        ? 1 / (10 ** collection.decimalPlaces)
        : 1
      const widget = preferredWidget(conceptId) || collection.defaultWidget || ''
      const inputId = `guided-question-${formatDisplayLabel(question.id).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`

      const playbackRate = (() => {
        if (media?.type !== 'animation') return 1
        const currentAnswer = Array.isArray(answer) ? answer[0] : answer
        if (currentAnswer === null || currentAnswer === undefined) return 1
        return media.playbackRates?.[String(currentAnswer)] ?? 1
      })()

      const validationResult = validationResults.value[conceptId]
      const validationSeverity = validationResult?.severity ?? null
      let validationMessageToken = ''
      if (validationResult?.severity === 'warning') validationMessageToken = WARNING_MESSAGE_TOKEN
      if (validationResult?.severity === 'blocking' || validationResult?.severity === 'unconfigured') validationMessageToken = BLOCKING_MESSAGE_TOKEN

      // Blood pressure draft (per conceptId). Ranges, systolic/diastolic text values,
      // and the unknown flag are mirrored into the item so the specialised BP input
      // renders the exact same way as in the standalone question view.
      let bloodPressureRanges: GuidedBloodPressureRange | null = null
      let bloodPressureSystolic = ''
      let bloodPressureDiastolic = ''
      let bloodPressureSystolicInvalid = false
      let bloodPressureDiastolicInvalid = false
      let bloodPressureSystolicWarning = false
      let bloodPressureDiastolicWarning = false
      let bloodPressureUnknown = false
      if (collectionType === 'blood_pressure') {
        bloodPressureRanges = collection.systolic && collection.diastolic && collection.unit === 'mmHg'
          ? {
              unit: collection.unit,
              systolic: collection.systolic,
              diastolic: collection.diastolic,
            }
          : null
        const draft = bloodPressureDrafts.value[conceptId]
        bloodPressureUnknown = draft?.unknown ?? answer === 'unknown'
        if (!bloodPressureUnknown) {
          const storedAnswer = typeof answer === 'string' && answer.toLowerCase() !== 'unknown'
            ? answer
            : ''
          const parts = storedAnswer.split('/')
          bloodPressureSystolic = draft?.systolic || parts[0] || ''
          bloodPressureDiastolic = draft?.diastolic || parts[1] || ''
          const validation = bloodPressureValidations.value[conceptId]
            ?? validateGuidedBloodPressure(
              bloodPressureRanges,
              bloodPressureSystolic,
              bloodPressureDiastolic,
            )
          const relationshipInvalid = validation?.code === 'invalid_relationship'
            || validation?.code === 'invalid_format'
            || validation?.code === 'invalid_configuration'
          const systolicValidation = validateBloodPressurePart(
            bloodPressureRanges, 'systolic', bloodPressureSystolic,
          )
          const diastolicValidation = validateBloodPressurePart(
            bloodPressureRanges, 'diastolic', bloodPressureDiastolic,
          )
          bloodPressureSystolicInvalid = relationshipInvalid
            || systolicValidation?.severity === 'blocking'
            || systolicValidation?.severity === 'unconfigured'
          bloodPressureDiastolicInvalid = relationshipInvalid
            || diastolicValidation?.severity === 'blocking'
            || diastolicValidation?.severity === 'unconfigured'
          bloodPressureSystolicWarning = validation?.severity === 'warning'
            && systolicValidation?.severity === 'warning'
          bloodPressureDiastolicWarning = validation?.severity === 'warning'
            && diastolicValidation?.severity === 'warning'
        }
      }

      // Date draft (per conceptId). Both `date` and `calculated_date` use the same draft
      // so the input value persists across step transitions and visibility changes.
      let dateValue = ''
      let dateMin = ''
      let dateMax = ''
      let dateInvalid = false
      const dateCalculated = collectionType === 'calculated_date'
      if (collectionType === 'date' || dateCalculated) {
        const draft = dateDrafts.value[conceptId]
        const rawAnswer = typeof answer === 'string' ? answer.slice(0, 10) : ''
        dateValue = draft?.value || rawAnswer
        dateMin = dateFromTodayOffset(collection.minOffset)
        dateMax = dateFromTodayOffset(collection.maxOffset)
        if (collectionType === 'date') {
          dateInvalid = dateValue !== ''
            && formatDateAnswer(dateValue, dateMin, dateMax) === null
        }
      }

      return {
        conceptId,
        prompt: questionPromptFor(question),
        options,
        collection,
        collectionType,
        answer,
        selectedValues,
        multiple: collection.selection === 'multiple',
        displayUnit,
        numericStep,
        widget,
        inputId,
        media,
        playbackRate,
        validationSeverity,
        validationMessageToken,
        allowUnknown: collection.allowUnknown === true,
        bloodPressureRanges,
        bloodPressureSystolic,
        bloodPressureDiastolic,
        bloodPressureSystolicInvalid,
        bloodPressureDiastolicInvalid,
        bloodPressureSystolicWarning,
        bloodPressureDiastolicWarning,
        bloodPressureUnknown,
        dateValue,
        dateMin,
        dateMax,
        dateInvalid,
        dateCalculated,
      }
    })
  })
  
  // Check if all questions in the sequential group are answered
  const allSequentialGroupAnswered = computed(() => {
    if (!isSequentialGroup.value) return false
    return sequentialGroupQuestionItems.value.every(item => {
      const answer = answers.value[item.conceptId]
      const hasAnswer = answer !== undefined && answer !== null && answer !== ''
      if (item.collectionType === 'calculated_date' && !hasAnswer) return true
      return hasAnswer
    })
  })
  watch(() => calculatedDateAnswers(definition.value, answers.value), calculated => {
    const next = { ...answers.value }
    const invalid = hiddenConcepts(answers.value)
    let changed = false
    for (const [conceptId, calculatedValue] of Object.entries(calculated)) {
      const value = invalid.has(conceptId) ? undefined : calculatedValue
      if (next[conceptId] === value) continue
      changed = true
      if (value === undefined) delete next[conceptId]
      else next[conceptId] = value
    }
    if (changed) answers.value = next
  }, { immediate: true, flush: 'sync' })
  const currentGroup = computed(() => currentStep.value?.kind === 'group' ? currentStep.value : null)
  const groupMedia = computed<GuidedModeMedia | null>(() => currentGroup.value?.media || null)

  // For sequential groups, use the nested step; otherwise use the current step directly
  const effectiveStep = computed(() => {
    if (isSequentialGroup.value && currentNestedStep.value) {
      return currentNestedStep.value
    }
    return currentStep.value
  })
  
  const currentQuestion = computed(() => effectiveStep.value ? questionForStep(definition.value, effectiveStep.value) : undefined)
  const questionMedia = computed<GuidedModeMedia | null>(() => effectiveStep.value?.media || currentStep.value?.media || null)
  const currentConceptId = computed(() => currentQuestion.value?.conceptId || '')
  const currentConcept = computed(() => definition.value?.concepts?.[currentConceptId.value] || null)
  const currentAnswer = computed(() => answers.value[currentConceptId.value])
  const currentInputAnswer = computed(() => (
    Object.prototype.hasOwnProperty.call(numericDraftAnswers.value, currentConceptId.value)
      ? numericDraftAnswers.value[currentConceptId.value]
      : currentAnswer.value
  ))
  const questionPlaybackRate = computed(() => {
    if (questionMedia.value?.type !== 'animation') return 1
    const answer = Array.isArray(currentAnswer.value) ? currentAnswer.value[0] : currentAnswer.value
    if (answer === null || answer === undefined) return 1
    return questionMedia.value.playbackRates?.[String(answer)] ?? 1
  })
  const questionCollection = computed<GuidedModeCollection>(() => effectiveStep.value?.collection ?? currentStep.value?.collection ?? {})
  const questionCollectionType = computed(() => questionCollection.value.type || '')
  const questionAllowsUnknown = computed(() => questionCollection.value.allowUnknown === true)
  const questionAllowsMultiple = computed(() => questionCollection.value.selection === 'multiple')
  const questionTitle = computed(() => questionTitleFor(currentQuestion.value))
  const questionPrompt = computed(() => questionPromptFor(currentQuestion.value))
  const questionInstructions = computed(() => {
    // For sequential groups, don't show group-level instructions (user sees one question at a time)
    // For multiselect groups, show the instructions
    if (isSequentialGroup.value) return ''
    const token = currentGroup.value?.instructionsToken || currentQuestion.value?.instructionsToken
    return token ? translate(token, '') : ''
  })
  const descPrompt = computed(() => {
    if (!currentGroup.value || isSequentialGroup.value) return ''
    const token = currentGroup.value.descToken || 'group.general.multiple.select.prompt'
    return translate(token, '')
  })
  const groupPrompt = computed(() => currentGroup.value?.titleToken
    ? translate(currentGroup.value.titleToken, formatDisplayLabel(currentGroup.value.id || 'Questions'))
    : '')
  const groupOptions = computed(() => {
    if (!currentGroup.value) return []
    const values = visibleGroupSteps(currentGroup.value).flatMap(step => {
      const question = questionForStep(definition.value, step)
      return question ? [{ value: question.conceptId, label: questionTitleFor(question) }] : []
    })
    const noneOption = currentGroup.value.noneOption
    return noneOption
      ? [...values, { value: noneOption.value, label: translate(noneOption.token, formatDisplayLabel(noneOption.value)) }]
      : values
  })
  const selectedGroupOptions = computed(() => {
    if (!currentGroup.value) return []
    const conceptIds = groupConceptIds(currentGroup.value)
    const selected = conceptIds.filter(conceptId => answers.value[conceptId] === 'yes')
    const allAnsweredNo = conceptIds.length > 0 && conceptIds.every(conceptId => answers.value[conceptId] === 'no')
    return allAnsweredNo && currentGroup.value.noneOption ? [currentGroup.value.noneOption.value] : selected
  })
  const questionOptions = computed(() => {
    if (!currentQuestion.value) return []
    const responseTokens = currentQuestion.value.responseTokens || {}
    const values = Object.keys(responseTokens).length
      ? Object.keys(responseTokens)
      : (currentConcept.value?.value?.values || []).map(item => item.value)
    return values.map(value => ({
      value,
      label: translate(
        responseTokens[value] || `responses.${currentQuestion.value!.id.replace('question.', '')}.${value}.title`,
        formatDisplayLabel(value),
      ),
    }))
  })
  const selectedOptions = computed(() => Array.isArray(currentAnswer.value)
    ? currentAnswer.value.map(String)
    : currentAnswer.value === undefined ? [] : [String(currentAnswer.value)])
  const displayUnit = computed(() => questionCollection.value.displayUnit
    ? translate(`units.${questionCollection.value.displayUnit}.short`, questionCollection.value.displayUnit)
    : '')
  const numericStep = computed(() => questionCollection.value.decimalPlaces
    ? 1 / (10 ** questionCollection.value.decimalPlaces)
    : 1)
  const questionWidget = computed(() => widgetOverrides.value[currentConceptId.value]
    || preferredWidget(currentConceptId.value)
    || questionCollection.value.defaultWidget
    || '')
  const inputId = computed(() => `guided-question-${formatDisplayLabel(currentQuestion.value?.id || 'answer').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`)
  
  // Check if we're on the last question (considering nested questions in sequential groups)
  const isLastQuestion = computed(() => {
    if (isSequentialGroup.value) {
      return isLastInSequentialGroup.value && currentStepIndex.value >= visibleSteps.value.length - 1
    }
    return currentStepIndex.value >= visibleSteps.value.length - 1
  })
  
  const currentValidation = computed(() => validationResults.value[currentConceptId.value] ?? null)
  const hasBlockingValidation = computed(() => currentValidation.value?.severity === 'blocking'
    || currentValidation.value?.severity === 'unconfigured')
  
  // For sequential groups, can continue if ALL questions are answered
  // For non-sequential, can continue if current question is answered
  const questionCanContinue = computed(() => {
    // Sequential groups require all questions answered
    if (isSequentialGroup.value) {
      if (allSequentialGroupAnswered.value) return true
      // Check if there are no blocking validations
      const items = sequentialGroupQuestionItems.value
      for (const item of items) {
        const validation = validationResults.value[item.conceptId]
        if (validation?.severity === 'blocking' || validation?.severity === 'unconfigured') {
          return false
        }
      }
      return allSequentialGroupAnswered.value
    }
    
    // Non-sequential: check current question
    if (!hasBlockingValidation.value && effectiveStep.value) {
      return isStepAnswered(effectiveStep.value, definition.value, answers.value)
    }
    return false
  })
  const questionValidationMessageToken = computed(() => {
    if (currentValidation.value?.severity === 'warning') return WARNING_MESSAGE_TOKEN
    return hasBlockingValidation.value ? BLOCKING_MESSAGE_TOKEN : ''
  })
  const bloodPressureRanges = computed(() => {
    const collection = questionCollection.value
    if (!collection.systolic || !collection.diastolic || collection.unit !== 'mmHg') return null
    return {
      unit: collection.unit,
      systolic: collection.systolic,
      diastolic: collection.diastolic,
    }
  })
  const bloodPressureSystolicValidation = computed(() => validateBloodPressurePart(
    bloodPressureRanges.value,
    'systolic',
    bloodPressureSystolic.value,
  ))
  const bloodPressureDiastolicValidation = computed(() => validateBloodPressurePart(
    bloodPressureRanges.value,
    'diastolic',
    bloodPressureDiastolic.value,
  ))
  const bloodPressureRelationshipInvalid = computed(() => (
    currentValidation.value?.code === 'invalid_relationship'
    || currentValidation.value?.code === 'invalid_format'
    || currentValidation.value?.code === 'invalid_configuration'
  ))
  const bloodPressureSystolicInvalid = computed(() => bloodPressureRelationshipInvalid.value
    || bloodPressureSystolicValidation.value?.severity === 'blocking'
    || bloodPressureSystolicValidation.value?.severity === 'unconfigured')
  const bloodPressureDiastolicInvalid = computed(() => bloodPressureRelationshipInvalid.value
    || bloodPressureDiastolicValidation.value?.severity === 'blocking'
    || bloodPressureDiastolicValidation.value?.severity === 'unconfigured')
  const bloodPressureSystolicWarning = computed(() => currentValidation.value?.severity === 'warning'
    && bloodPressureSystolicValidation.value?.severity === 'warning')
  const bloodPressureDiastolicWarning = computed(() => currentValidation.value?.severity === 'warning'
    && bloodPressureDiastolicValidation.value?.severity === 'warning')
  const guidedDateMin = computed(() => dateFromTodayOffset(questionCollection.value.minOffset))
  const guidedDateMax = computed(() => dateFromTodayOffset(questionCollection.value.maxOffset))
  const guidedDateInvalid = computed(() => guidedDateValue.value !== ''
    && formatDateAnswer(guidedDateValue.value, guidedDateMin.value, guidedDateMax.value) === null)
  const answeredItems = computed<GuidedModeAnsweredItem[]>(() => visibleSteps.value.reduce<GuidedModeAnsweredItem[]>((items, step, stepIndex) => {
    const questionSteps = step.kind === 'group' ? step.steps || [] : [step]
    for (const questionStep of questionSteps) {
      const question = questionForStep(definition.value, questionStep)
      const answer = question && answers.value[question.conceptId]
      const answered = Array.isArray(answer) ? answer.length > 0 : answer !== undefined && answer !== null && answer !== ''
      if (question && answered) items.push({
        question,
        answer: formatAnswer(answer, question, questionStep),
        prompt: questionPromptFor(question),
        title: questionTitleFor(question),
        stepIndex,
        sourceGroupId: questionStep.sourceGroupId,
        groupTitle: step.titleToken,
      })
    }

    return items
  }, []))
  const summaryBmi = computed<number | null>(() => {
    const values: Record<string, GuidedAnswer> = {}
    for (const item of answeredItems.value) {
      const code = definition.value?.concepts[item.question.conceptId]?.code?.code
        || item.question.conceptId.replace(/^concept\./, '')
      values[code] = answers.value[item.question.conceptId]
    }
    const bmi = calculateBMI(values.height, values.weight, values.weight_unknown)
    return typeof bmi === 'number' && Number.isFinite(bmi) ? bmi : null
  })

  function questionTitleFor(question?: GuidedModeQuestionDefinition) {
    return question ? translate(question.titleToken, formatDisplayLabel(question.id.replace('question.', ''))) : ''
  }
  function questionPromptFor(question?: GuidedModeQuestionDefinition) {
    return question ? translate(question.promptToken, formatDisplayLabel(question.id.replace('question.', ''))) : ''
  }
  function formatAnswer(answer: GuidedAnswer, question: GuidedModeQuestionDefinition, step: GuidedModeStep) {
    const collection = step.collection
    const concept = definition.value?.concepts[question.conceptId]
    return (Array.isArray(answer) ? answer : [answer])
      .filter((value): value is string | number => value !== undefined)
      .map(value => String(value).toLowerCase() === 'unknown'
        ? translate('responses.generic.unknown.title', "I don't know")
        : question.responseTokens?.[String(value)]
          ? translate(question.responseTokens[String(value)], formatDisplayLabel(String(value)))
          : formatValue(value))
      .join(' · ')

    function formatValue(value: string | number) {
      if (concept?.value?.type === 'date' || collection?.type === 'date' || collection?.type === 'calculated_date') {
        const day = String(value).slice(0, 10)
        const date = new Date(`${day}T00:00:00`)
        return /^\d{4}-\d{2}-\d{2}$/.test(day) && Number.isFinite(date.getTime())
          ? (options.formatDate?.(date) ?? date.toLocaleDateString()) : String(value)
      }
      const unit = collection?.displayUnit || collection?.unit
      const numeric = collection?.type === 'numeric'
      const text = numeric && Number.isFinite(Number(value))
        ? (options.formatNumber?.(Number(value)) ?? String(value)) : String(value)
      return unit ? `${text} ${translate(`units.${unit}.title`, unit)}` : text
    }
  }
  function groupConceptIds(group: GuidedModeStep) {
    return visibleGroupSteps(group).flatMap(step => {
      const question = questionForStep(definition.value, step)
      return question ? [question.conceptId] : []
    })
  }
  function visibleGroupSteps(group: GuidedModeStep) {
    const settled = settledConceptIds?.value || []
    return (group.steps || []).filter(step => isStepVisible(step, answers.value)
      && !settled.includes(questionForStep(definition.value, step)?.conceptId || ''))
  }
  // Collect every conceptId associated with `step`, including nested questions
  // inside groups and any `outputs` (selectedValue/unselectedValue mappings
  // produced by multi-option parents such as the symptoms multiselect).
  // Used by the visibility watcher to know which answers belong to the current
  // visible set so it can drop the rest.
  function conceptIdsForStep(step: GuidedModeStep): string[] {
    if (!definition.value) return []
    if (step.kind === 'group') {
      const ids: string[] = []
      for (const nested of step.steps || []) {
        ids.push(...conceptIdsForStep(nested))
      }
      return ids
    }
    const question = questionForStep(definition.value, step)
    if (!question) return []
    return [
      question.conceptId,
      ...(question.outputs || []).map(output => output.conceptId),
    ]
  }
  function clearAutoAdvanceTimer() {
    if (pendingAutoAdvanceTimer.value !== null) {
      clearTimeout(pendingAutoAdvanceTimer.value)
      pendingAutoAdvanceTimer.value = null
    }
  }
  function scheduleAutoAdvance(canAdvance = () => true) {
    clearAutoAdvanceTimer()
    pendingAutoAdvanceTimer.value = setTimeout(() => {
      pendingAutoAdvanceTimer.value = null
      if (autoAdvanceInFlight || !canAdvance()) return
      autoAdvanceInFlight = true
      void Promise.resolve(onAutoAdvance())
        .catch(() => undefined)
        .finally(() => {
          autoAdvanceInFlight = false
        })
    }, AUTO_PROGRESS_DELAY_MS)
  }
  function evaluateAutoProgress(answer: GuidedAnswer, selectedValues: string[]) {
    if (autoProgress.value && shouldAutoProgress({
      answer,
      collection: questionCollection.value,
      selectedOptions: selectedValues,
    })) scheduleAutoAdvance()
  }
  function afterAnswerChanged(answer: GuidedAnswer, selectedValues: string[]) {
    if (onAnswerChanged()) {
      clearAutoAdvanceTimer()
      return
    }
    evaluateAutoProgress(answer, selectedValues)
  }
  function afterSequentialGroupAnswerChanged(conceptId: string, answer: GuidedAnswer) {
    clearAutoAdvanceTimer()
    if (onAnswerChanged()) return

    // Focused assessment and skip logic can leave a group with only one question.
    // Apply the same input policy as standalone questions, using the visible count.
    const group = currentStep.value
    const canAdvance = () => {
      const steps = sequentialGroupSteps.value
      const step = steps[0]
      return autoProgress.value && currentStep.value === group && steps.length === 1
        && questionForStep(definition.value, step)?.conceptId === conceptId
        && shouldAutoProgress({
          answer,
          collection: step.collection || {},
          selectedOptions: Array.isArray(answer) ? answer.map(String)
            : answer === null || answer === undefined ? [] : [String(answer)],
        })
    }
    if (canAdvance()) scheduleAutoAdvance(canAdvance)
  }
  function setValidation(result: GuidedValidationResult | null) {
    const nextResults = { ...validationResults.value }
    if (result) nextResults[currentConceptId.value] = result
    else delete nextResults[currentConceptId.value]
    validationResults.value = nextResults
  }
  // Per-conceptId equivalent of `setValidation`. Used by the sequential-group
  // answer path which doesn't operate on `currentConceptId` (the group has
  // several nested questions that may all need their own validation state).
  function writeSequentialGroupValidation(
    conceptId: string,
    result: GuidedValidationResult | null,
  ) {
    const nextResults = { ...validationResults.value }
    if (result) nextResults[conceptId] = result
    else delete nextResults[conceptId]
    validationResults.value = nextResults
  }
  function setAcceptedAnswer(value: GuidedAnswer) {
    answers.value = { ...answers.value, [currentConceptId.value]: value }
  }
  function removeAcceptedAnswer() {
    const nextAnswers = { ...answers.value }
    delete nextAnswers[currentConceptId.value]
    answers.value = nextAnswers
  }
  function handleValidatedAnswer(
    value: GuidedAnswer,
    validation: GuidedValidationResult | null,
    userInitiated: boolean,
  ) {
    setValidation(validation)
    if (validation?.severity === 'blocking' || validation?.severity === 'unconfigured') {
      removeAcceptedAnswer()
      clearAutoAdvanceTimer()
      return
    }

    setAcceptedAnswer(value)
    if (!userInitiated) return
    const values = Array.isArray(value)
      ? value.map(String)
      : value === null || value === undefined ? [] : [String(value)]
    if (validation?.severity === 'warning') {
      clearAutoAdvanceTimer()
      onAnswerChanged()
      return
    }
    afterAnswerChanged(value, values)
  }
  function setAnswer(value: GuidedAnswer, userInitiated = true) {
    if (questionCollectionType.value !== 'numeric') {
      handleValidatedAnswer(value, null, userInitiated)
      return
    }

    numericDraftAnswers.value = {
      ...numericDraftAnswers.value,
      [currentConceptId.value]: value,
    }
    if (value === null || value === undefined || value === '') {
      handleValidatedAnswer(null, null, userInitiated)
      return
    }
    if (typeof value === 'string' && value.toLowerCase() === 'unknown') {
      handleValidatedAnswer(value, null, userInitiated)
      return
    }

    const validation = validateObservationValue(
      questionCollection.value,
      value,
    ) as GuidedValidationResult
    handleValidatedAnswer(value, validation, userInitiated)
  }
  function selectOption(value: string) {
    const multiple = questionCollection.value.selection === 'multiple'
    const values = multiple
      ? toggleMultipleOption(selectedOptions.value.map(String), value, questionCollection.value.exclusiveValues)
      : [value]
    const answer = multiple ? values : value
    answers.value = {
      ...answers.value,
      [currentConceptId.value]: answer,
      ...buildQuestionOutputAnswers(values, currentQuestion.value?.outputs),
    }
    afterAnswerChanged(answer, values)
  }
  
  // Handle answer change for a specific concept in a sequential group.
  //
  // Unlike `setAnswer`, this entry point used to validate the value purely as
  // a gate: a blocking result was `return`-ed silently (no validation message
  // ever reached the question UI), and a warning was never persisted to
  // `validationResults`, so the user could submit it without seeing the
  // confirmation prompt the standalone path shows. To match `setAnswer` we
  // factor the per-question validation flow through `validationResults` and
  // `numericDraftAnswers`, keyed by the affected `conceptId` so each nested
  // item keeps its own state.
  function setSequentialGroupAnswer(conceptId: string, value: GuidedAnswer) {
    clearAutoAdvanceTimer()
    const step = sequentialGroupSteps.value.find(s => {
      const q = questionForStep(definition.value, s)
      return q?.conceptId === conceptId
    })
    if (!step) return

    const question = questionForStep(definition.value, step)
    if (!question) return

    const collection = step.collection || {}
    const isNumeric = collection.type === 'numeric'
    const isUnknown = typeof value === 'string' && value.toLowerCase() === 'unknown'
    const isClearingAnswer = value === null || value === undefined || value === ''

    if (isNumeric) {
      // Mirror `setAnswer` so the numeric draft persists across re-renders and
      // visibility changes, regardless of the validation outcome. This keeps
      // the slider/text input showing what the user typed even when we drop
      // the value because it's out of range.
      numericDraftAnswers.value = {
        ...numericDraftAnswers.value,
        [conceptId]: value,
      }
    }

    // Unknown is always persisted and bypasses range validation — there is no
    // numeric draft to drop and the value is meaningful ("I don't know").
    if (isNumeric && isUnknown) {
      writeSequentialGroupValidation(conceptId, { severity: 'normal', code: 'normal', value: '' })
      answers.value = { ...answers.value, [conceptId]: value }
      afterSequentialGroupAnswerChanged(conceptId, value)
      return
    }

    if (isNumeric) {
      if (isClearingAnswer) {
        // Clearing the input matches the standalone path: nothing to validate
        // and nothing to persist as an answer.
        writeSequentialGroupValidation(conceptId, null)
        const nextAnswers = { ...answers.value }
        delete nextAnswers[conceptId]
        answers.value = nextAnswers
        return
      }

      const validation = validateObservationValue(collection, value) as GuidedValidationResult
      // Persist the validation result so the specialised input can render the
      // matching message/invalid styling. Previously this outcome was
      // discarded, leaving the UI blind to why the answer was rejected.
      writeSequentialGroupValidation(conceptId, validation)

      const blocking = validation?.severity === 'blocking'
        || validation?.severity === 'unconfigured'
      if (blocking) {
        const nextAnswers = { ...answers.value }
        delete nextAnswers[conceptId]
        answers.value = nextAnswers
        return
      }
    }

    answers.value = { ...answers.value, [conceptId]: value }
    afterSequentialGroupAnswerChanged(conceptId, value)
  }
  
  // Handle option selection for a specific concept in sequential group
  function selectSequentialGroupOption(conceptId: string, value: string) {
    const step = sequentialGroupSteps.value.find(s => {
      const q = questionForStep(definition.value, s)
      return q?.conceptId === conceptId
    })
    if (!step) return
    
    const question = questionForStep(definition.value, step)
    if (!question) return
    
    const collection = step.collection || {}
    const currentAnswer = answers.value[conceptId]
    const multiple = collection.selection === 'multiple'
    const selectedValues = Array.isArray(currentAnswer) ? currentAnswer.map(String) : currentAnswer === undefined ? [] : [String(currentAnswer)]
    
    const newValues = multiple
      ? toggleMultipleOption(selectedValues, value, collection.exclusiveValues)
      : [value]
    const answer = multiple ? newValues : value
    
    answers.value = {
      ...answers.value,
      [conceptId]: answer,
      ...buildQuestionOutputAnswers(newValues, question.outputs),
    }
    afterSequentialGroupAnswerChanged(conceptId, answer)
  }

  // Resolve ranges/validity for a sequential-group blood pressure question.
  function resolveBloodPressureRanges(conceptId: string): GuidedBloodPressureRange | null {
    const step = sequentialGroupSteps.value.find(s => {
      const q = questionForStep(definition.value, s)
      return q?.conceptId === conceptId
    })
    if (!step) return null
    const collection = step.collection || {}
    if (collection.type !== 'blood_pressure') return null
    if (!collection.systolic || !collection.diastolic || collection.unit !== 'mmHg') return null
    return { unit: collection.unit, systolic: collection.systolic, diastolic: collection.diastolic }
  }

  // Persist the systolic/diastolic draft for a blood pressure question inside a
  // sequential group so each item keeps its own input state.
  function setSequentialGroupBloodPressurePart(
    conceptId: string, part: BloodPressurePart, value: string,
  ) {
    clearAutoAdvanceTimer()
    const ranges = resolveBloodPressureRanges(conceptId)
    if (!ranges) return
    const previous = bloodPressureDrafts.value[conceptId]
      || { systolic: '', diastolic: '', unknown: false }
    const draft: BloodPressureDraft = {
      systolic: part === 'systolic' ? value : previous.systolic,
      diastolic: part === 'diastolic' ? value : previous.diastolic,
      unknown: false,
    }
    bloodPressureDrafts.value = { ...bloodPressureDrafts.value, [conceptId]: draft }
    const validation = validateGuidedBloodPressure(ranges, draft.systolic, draft.diastolic)
    bloodPressureValidations.value = {
      ...bloodPressureValidations.value,
      [conceptId]: validation,
    }
    const formatted = draft.systolic && draft.diastolic
      ? `${Number(draft.systolic)}/${Number(draft.diastolic)}`
      : null
    // Persist via the standard answers map and re-run follow-up logic so
    // group-level completion tracking stays consistent with option/numeric
    // answers.
    const blocking = validation?.severity === 'blocking'
      || validation?.severity === 'unconfigured'
    if (blocking) {
      const nextAnswers = { ...answers.value }
      delete nextAnswers[conceptId]
      answers.value = nextAnswers
      validationResults.value = { ...validationResults.value, [conceptId]: validation }
      return
    }
    answers.value = { ...answers.value, [conceptId]: formatted }
    validationResults.value = {
      ...validationResults.value,
      [conceptId]: validation ?? { severity: 'normal', code: 'normal', value: '' },
    }
    onAnswerChanged()
  }

  function setSequentialGroupUnknownAnswer(conceptId: string) {
    if (!resolveBloodPressureRanges(conceptId)) return
    bloodPressureDrafts.value = {
      ...bloodPressureDrafts.value,
      [conceptId]: { systolic: '', diastolic: '', unknown: true },
    }
    answers.value = { ...answers.value, [conceptId]: 'unknown' }
    validationResults.value = {
      ...validationResults.value,
      [conceptId]: { severity: 'normal', code: 'normal', value: '' },
    }
    afterSequentialGroupAnswerChanged(conceptId, 'unknown')
  }

  function setSequentialGroupDate(conceptId: string, value: string) {
    clearAutoAdvanceTimer()
    const step = sequentialGroupSteps.value.find(s => {
      const q = questionForStep(definition.value, s)
      return q?.conceptId === conceptId
    })
    if (!step) return
    const collection = step.collection || {}
    if (collection.type !== 'date' && collection.type !== 'calculated_date') return
    const min = dateFromTodayOffset(collection.minOffset)
    const max = dateFromTodayOffset(collection.maxOffset)
    const formatted = collection.type === 'date'
      ? formatDateAnswer(value, min, max)
      : (value || null)
    dateDrafts.value = { ...dateDrafts.value, [conceptId]: { value } }
    answers.value = { ...answers.value, [conceptId]: formatted }
    onAnswerChanged()
  }
  function selectGroupOption(value: string) {
    if (!currentGroup.value) return
    clearAutoAdvanceTimer()
    const noneValue = currentGroup.value.noneOption?.value
    const conceptIds = groupConceptIds(currentGroup.value)
    const nextAnswers = { ...answers.value }
    if (value === noneValue) {
      for (const conceptId of conceptIds) nextAnswers[conceptId] = 'no'
    } else {
      for (const conceptId of conceptIds) {
        nextAnswers[conceptId] = conceptId === value
          ? (answers.value[conceptId] === 'yes' ? 'no' : 'yes')
          : (answers.value[conceptId] ?? 'no')
      }
    }
    answers.value = nextAnswers
    if (onAnswerChanged()) return
    if (autoProgress.value && value === noneValue) scheduleAutoAdvance()
  }
  function setBloodPressurePart(part: BloodPressurePart, value: string) {
    if (part === 'systolic') bloodPressureSystolic.value = value
    else bloodPressureDiastolic.value = value
    const validation = validateGuidedBloodPressure(
      bloodPressureRanges.value,
      bloodPressureSystolic.value,
      bloodPressureDiastolic.value,
    )
    const formatted = bloodPressureSystolic.value && bloodPressureDiastolic.value
      ? `${Number(bloodPressureSystolic.value)}/${Number(bloodPressureDiastolic.value)}`
      : null
    handleValidatedAnswer(formatted, validation, true)
  }
  function setUnknownAnswer() {
    bloodPressureSystolic.value = ''
    bloodPressureDiastolic.value = ''
    handleValidatedAnswer('unknown', null, true)
  }
  function setGuidedDate(value: string) {
    guidedDateValue.value = value
    setAnswer(formatDateAnswer(value, guidedDateMin.value, guidedDateMax.value))
  }
  function syncSpecialInputState(step: GuidedModeStep | null, initializeMissing = false) {
    const question = step ? questionForStep(definition.value, step) : undefined
    const answer = question ? answers.value[question.conceptId] : undefined
    if (step?.collection?.type === 'blood_pressure') {
      const parts = typeof answer === 'string' && answer.toLowerCase() !== 'unknown' ? answer.split('/') : []
      bloodPressureSystolic.value = parts[0] || ''
      bloodPressureDiastolic.value = parts[1] || ''
      if (answer === undefined) {
        if (initializeMissing) handleValidatedAnswer(null, null, false)
      } else if (answer !== 'unknown') {
        const validation = validateGuidedBloodPressure(
          bloodPressureRanges.value,
          bloodPressureSystolic.value,
          bloodPressureDiastolic.value,
        )
        handleValidatedAnswer(answer, validation, false)
      }
    }
    if (step?.collection?.type === 'date') {
      guidedDateValue.value = typeof answer === 'string' ? answer.slice(0, 10) : ''
      if (initializeMissing && answer === undefined) setAnswer(null, false)
    }
  }
  function setQuestionWidget(widget: string) {
    if (!currentConceptId.value || !widget) return
    widgetOverrides.value = { ...widgetOverrides.value, [currentConceptId.value]: widget }
  }
  function resetQuestionnaire() {
    clearAutoAdvanceTimer()
    answers.value = {}
    currentIndex.value = 0
    currentNestedIndex.value = 0
    widgetOverrides.value = {}
    numericDraftAnswers.value = {}
    validationResults.value = {}
    bloodPressureDrafts.value = {}
    dateDrafts.value = {}
    bloodPressureValidations.value = {}
  }
  
  // Advance to the next question within a sequential group
  function advanceSequentialGroup() {
    if (!isSequentialGroup.value) return false
    
    if (isLastInSequentialGroup.value) {
      // At the last question in the group, signal to move to the next step
      return true // Signal to move to next step
    } else {
      // Move to the next nested question within the group
      currentNestedIndex.value++
      return false // Signal to stay on current step (but with new nested index)
    }
  }
  
  // Reset nested index when moving to a new group
  function resetNestedIndex() {
    currentNestedIndex.value = 0
  }

  // When answers change, the visibility filter may drop nested steps from the
  // group (e.g., answering "are you menstruating?" with "no" hides the
  // amenorrhea follow-up). Clamp `currentNestedIndex` to the new length so the
  // UI doesn't point past the end of the filtered list.
  watch(sequentialGroupSteps, steps => {
    if (currentNestedIndex.value >= steps.length) {
      currentNestedIndex.value = Math.max(0, steps.length - 1)
    }
  })

  // Drop answers (and any draft/validation/widget state keyed by conceptId)
  // for steps that are no longer visible. Without this, toggling a parent
  // answer off (e.g. `coughing_yesno = no` after `coughing_duration = 10`)
  // leaves a stale value in `answers.value`. When the parent is toggled back
  // on, the follow-up step renders with the old value instead of starting
  // empty. The legacy v5/v6 panel flow calls `reset` for skipped panels
  // (see `clients/app/v5|6/.../store/v4/modules/panel/actions.js`); this
  // watcher mirrors that behaviour for the guided questionnaire. The submit
  // path and MRX both read these answers, so invalid dependent values must
  // leave canonical state as well as the UI.
  //
  // We watch `answers` (instead of `visibleSteps`) because visibility can
  // depend on a step that lives inside the same group as the gated step.
  // The top-level `visibleSteps` only changes when a whole group becomes
  // visible/hidden; it does not move when a nested child's `visibleWhen`
  // flips. Driving the cleanup off `answers` lets us re-evaluate every
  // affected conceptId whenever the user touches any control — and the
  // early-return + Set comparison below keeps that cheap when nothing
  // actually changed.
  //
  // Flatten the assessment so every nested step inside a group is
  // evaluated independently. `isStepVisible` only checks the step's own
  // `visibleWhen`, so a child step inside an otherwise-visible group can
  // still be hidden when its own rule fails.
  function hiddenConcepts(values: Record<string, GuidedAnswer>, removed = new Set<string>()): Set<string> {
    if (!definition.value) return new Set<string>()
    const permitted = new Map<string, boolean>()
    function negativeGate(rule: GuidedModeCondition | GuidedModeVisibilityRule): boolean {
      if ('all' in rule) return rule.all.some(negativeGate)
      if ('any' in rule) return rule.any.some(negativeGate)
      return values[rule.conceptId] === 'no' && !evaluateCondition(rule, values)
    }
    function knownGate(rule: GuidedModeCondition | GuidedModeVisibilityRule): boolean | undefined {
      if ('all' in rule) {
        const states = rule.all.map(knownGate)
        return states.includes(false) ? false : states.every(state => state === true) ? true : undefined
      }
      if ('any' in rule) {
        const states = rule.any.map(knownGate)
        return states.includes(true) ? true : states.every(state => state === false) ? false : undefined
      }
      if (values[rule.conceptId] === undefined) return removed.has(rule.conceptId) ? false : undefined
      return evaluateCondition(rule, values)
    }
    function visit(step: GuidedModeStep, ancestorVisible: boolean | undefined, parentNo = false) {
      let ownVisible = step.visibleWhen ? knownGate(step.visibleWhen) : true
      if (ownVisible === undefined && step.visibleWhen && negativeGate(step.visibleWhen)
        && !evaluateCondition(step.visibleWhen, values)) ownVisible = false
      const visible = ancestorVisible === false || ownVisible === false ? false
        : ancestorVisible === true && ownVisible === true ? true : undefined
      const closedByNo = parentNo || (ownVisible !== true && Boolean(step.visibleWhen && negativeGate(step.visibleWhen)))
      if (step.kind === 'group') {
        for (const child of step.steps || []) visit(child, visible, closedByNo)
      } else {
        for (const id of conceptIdsForStep(step)) {
          const preserved = keptSymptomConceptIds?.value.includes(id) && !closedByNo
          permitted.set(id, Boolean(permitted.get(id)) || visible !== false || Boolean(preserved))
        }
      }
    }
    for (const step of assessmentQuestionSteps(definition.value)) visit(step, true)
    return new Set(Array.from(permitted).filter(([, visible]) => !visible).map(([id]) => id))
  }
  const hiddenConceptIds = computed(() => hiddenConcepts(answers.value))

  function clearStaleAnswers(removedParents = new Set<string>()) {
    if (!definition.value) return
    const hidden = hiddenConcepts(answers.value, removedParents)
    if (hidden.size === 0) return

    let changed = false
    const nextAnswers = { ...answers.value }
    const nextNumericDrafts = { ...numericDraftAnswers.value }
    const nextValidations = { ...validationResults.value }
    const nextBpDrafts = { ...bloodPressureDrafts.value }
    const nextBpValidations = { ...bloodPressureValidations.value }
    const nextDateDrafts = { ...dateDrafts.value }
    const nextWidgetOverrides = { ...widgetOverrides.value }

    const answerStores = [
      nextAnswers, nextNumericDrafts, nextValidations, nextBpDrafts,
      nextBpValidations, nextDateDrafts, nextWidgetOverrides,
    ]
    const storedConceptIds = new Set(answerStores.flatMap(values => Object.keys(values)))
    const maxPasses = storedConceptIds.size
    const removedConcepts = new Set<string>(removedParents)
    const discarded = new Set<string>()
    for (let pass = 0; pass <= maxPasses; pass += 1) {
      let removed = false
      const invalid = hiddenConcepts(nextAnswers, removedConcepts)
      for (const conceptId of storedConceptIds) {
        // Demographics are not dependent assessment answers.
        if (DEMOGRAPHIC_CONCEPT_IDS.has(conceptId) || !invalid.has(conceptId)) continue
        if (!answerStores.some(values => Object.prototype.hasOwnProperty.call(values, conceptId))) continue
        for (const values of answerStores) delete values[conceptId]
        removedConcepts.add(conceptId)
        discarded.add(conceptId)
        changed = true
        removed = true
      }
      if (!removed) break
    }

    if (!changed) return
    answers.value = nextAnswers
    numericDraftAnswers.value = nextNumericDrafts
    validationResults.value = nextValidations
    bloodPressureDrafts.value = nextBpDrafts
    bloodPressureValidations.value = nextBpValidations
    dateDrafts.value = nextDateDrafts
    widgetOverrides.value = nextWidgetOverrides
    options.onAnswersRemoved?.(discarded)
  }

  watch(() => answers.value, (next, previous) => {
    const removed = new Set(Object.keys(previous || {}).filter(id => !(id in next)))
    clearStaleAnswers(removed)
  }, { flush: 'post' })

  watch(visibleSteps, steps => {
    if (currentIndex.value >= steps.length) currentIndex.value = Math.max(0, steps.length - 1)
  })
  
  // Reset nested index when step changes (e.g., moving to a different group)
  watch(currentStep, (newStep, oldStep) => {
    // Reset nested index if we're moving to a different group
    if (newStep?.id !== oldStep?.id) {
      currentNestedIndex.value = 0
    }
  }, { immediate: true })
  
  // Focused plans rebuild step objects as answers change. Initialise only for a different question.
  watch([() => effectiveStep.value ? stepKey(effectiveStep.value) : null, currentNestedIndex], () => {
    // Initialize numeric answer
    if (effectiveStep.value?.collection?.type === 'numeric' && currentQuestion.value?.conceptId) {
      const conceptId = currentQuestion.value.conceptId
      const value = Object.prototype.hasOwnProperty.call(numericDraftAnswers.value, conceptId)
        ? numericDraftAnswers.value[conceptId]
        : answers.value[conceptId] ?? null
      setAnswer(value, false)
    }
    syncSpecialInputState(effectiveStep.value, true)
  }, { immediate: true })

  return {
    // Basic state
    visibleSteps, currentStep, currentGroup, currentQuestion, currentConceptId, currentAnswer,
    currentInputAnswer, currentValidation, questionValidationMessageToken,
    // Collection & options
    questionMedia, questionPlaybackRate, groupMedia, questionCollection, questionCollectionType, questionAllowsUnknown,
    questionAllowsMultiple, questionTitle, questionPrompt, questionInstructions, descPrompt, groupPrompt, groupOptions, selectedGroupOptions,
    questionOptions, selectedOptions, displayUnit, numericStep, questionWidget, inputId, 
    // Navigation state
    isLastQuestion, questionCanContinue, 
    // Sequential group support
    isSequentialGroup, currentNestedIndex, sequentialGroupSteps, isLastInSequentialGroup,
    sequentialGroupQuestionItems, allSequentialGroupAnswered,
    // Summary
    answeredItems, summaryBmi, 
    // Blood pressure
    bloodPressureRanges, bloodPressureSystolic, bloodPressureDiastolic,
    bloodPressureSystolicInvalid, bloodPressureDiastolicInvalid,
    bloodPressureSystolicWarning, bloodPressureDiastolicWarning, 
    // Date
    guidedDateValue, guidedDateMin, guidedDateMax, guidedDateInvalid, 
    // Actions
    setAnswer, selectOption, selectGroupOption, setBloodPressurePart,
    setUnknownAnswer, setGuidedDate, syncSpecialInputState, setQuestionWidget, clearAutoAdvanceTimer,
    resetQuestionnaire, resetNestedIndex, clearStaleAnswers, hiddenConceptIds,
    // Sequential group actions
    setSequentialGroupAnswer, selectSequentialGroupOption,
    setSequentialGroupBloodPressurePart, setSequentialGroupUnknownAnswer,
    setSequentialGroupDate,
  }
}

function formatDisplayLabel(value: string) {
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())
}
